import type {
  PlanningCommand,
  MessageContext,
  ContextRequestType,
} from '~/types/planning';

export const serializeContextRequestCompletionContent = (
  requestType: ContextRequestType,
  requestId: string,
): string => JSON.stringify({ type: requestType, requestId });

export const parseContextRequestCompletionContent = (
  content: string,
): { requestType: ContextRequestType; requestId?: string } => {
  if (content === 'CLARIFICATION') {
    return { requestType: 'CLARIFICATION' };
  }
  try {
    const o = JSON.parse(content) as { type?: ContextRequestType; requestId?: string };
    if (o?.type === 'CLARIFICATION' && typeof o.requestId === 'string') {
      return { requestType: o.type, requestId: o.requestId };
    }
    if (o?.type === 'CLARIFICATION') {
      return { requestType: o.type };
    }
  } catch {
    /* legacy non-JSON */
  }
  if (content) {
    return { requestType: content as ContextRequestType };
  }
  return { requestType: 'CLARIFICATION' };
};

export const extractContextMetadata = (content: string): MessageContext => {
  const figmaPattern = /=== FIGMA DESIGN CONTEXT ===[\s\S]*?=== END FIGMA DESIGN CONTEXT ===/;
  const linearPattern = /=== LINEAR CONTEXT ===[\s\S]*?=== END LINEAR CONTEXT ===/;
  const commandPattern = /=== COMMAND: (\w+) ===/;
  
  const hasFigma = figmaPattern.test(content);
  const hasLinear = linearPattern.test(content);
  
  let figmaArtboardCount: number | undefined;
  let figmaArtboardNames: string[] | undefined;
  let linearIssueIdentifiers: string[] | undefined;
  let command: PlanningCommand | undefined;
  
  if (hasFigma) {
    const figmaMatch = content.match(figmaPattern);
    if (figmaMatch) {
      const artboardNameMatches = figmaMatch[0].matchAll(/ARTBOARD \d+:\s*([^\n]+)/g);
      const names = Array.from(artboardNameMatches, match => match[1].trim()).filter(Boolean);
      figmaArtboardNames = names.length > 0 ? names : undefined;
      figmaArtboardCount = names.length;
    }
  }
  
  if (hasLinear) {
    const linearMatch = content.match(linearPattern);
    if (linearMatch) {
      const issueIdMatches = linearMatch[0].matchAll(/Issue ID: ([^\n]+)/g);
      linearIssueIdentifiers = Array.from(issueIdMatches, match => match[1].trim());
    }
  }
  
  // Extract Linear issue details (title, status) from context
  const linearIssues: Array<{ identifier: string; title?: string; status?: string }> = [];
  if (hasLinear && linearIssueIdentifiers) {
    const linearMatch = content.match(linearPattern);
    if (linearMatch) {
      const contextText = linearMatch[0];
      linearIssueIdentifiers.forEach((identifier) => {
        // Extract title - look for "Title: ..." after the Issue ID
        const titleMatch = contextText.match(new RegExp(`Issue ID: ${identifier.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[\\s\\S]*?Title:\\s*([^\\n]+)`, 'i'));
        // Extract status - look for "Status: ..." 
        const statusMatch = contextText.match(new RegExp(`Issue ID: ${identifier.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[\\s\\S]*?Status:\\s*([^\\n]+)`, 'i'));
        
        linearIssues.push({
          identifier,
          title: titleMatch?.[1]?.trim(),
          status: statusMatch?.[1]?.trim()
        });
      });
    }
  }
  
  const commandMatch = content.match(commandPattern);
  if (commandMatch) {
    command = commandMatch[1] as PlanningCommand;
  }
  
  return {
    hasFigma,
    hasLinear,
    figmaArtboardCount,
    figmaArtboardNames,
    linearIssueIdentifiers,
    linearIssues: linearIssues.length > 0 ? linearIssues : undefined,
    command
  };
};

/**
 * Rebuild the attachment CHIPS for a persisted user message from its agent-only canvas markers.
 *
 * Live, a sent bubble shows attachment cards from optimistic state ("Slide 4", the reference image) — but
 * the backend persists only the prompt TEXT, and stripSystemContext removes the raw markers from display.
 * That left reloaded history with NO trace of what was attached: the words survived, the context vanished.
 * The markers themselves carry that context, so parse them back into the same chips instead of discarding
 * them. Image selections are markered as just "Image" (no src), so they render as a labelled chip without a
 * thumbnail — honest about what's known rather than guessing at a canvas that may have changed since.
 */
export const extractCanvasAttachmentCards = (
  content: string,
): { id: string; kind: 'file'; fileType: string; name: string; interactive: false; imageId?: number }[] => {
  const markers = content.match(/\[(?:Selected on canvas|Editing|Document ")[^\]]*\]/g)
  if (!markers) return [];
  const cards: { id: string; kind: 'file'; fileType: string; name: string; interactive: false; imageId?: number }[] = [];
  const seen = new Set<string>();
  const push = (fileType: string, name: string, key = `${fileType}:${name}`, imageId?: number) => {
    if (seen.has(key)) return;
    seen.add(key);
    cards.push({ id: `marker-${key}`, kind: 'file', fileType, name, interactive: false, ...(imageId != null ? { imageId } : {}) });
  };
  for (const marker of markers) {
    for (const m of marker.matchAll(/Slide (\d+)/g)) push('Slide', `Slide ${m[1]}`);
    for (const m of marker.matchAll(/Document "([^"]+)"/g)) push('Doc', m[1]);
    for (const m of marker.matchAll(/Note "([^"]+)"/g)) push('Note', m[1]);
    // Modern markers carry the canvas image id (`Image #4`) — one chip PER image, and the id lets the
    // canvas resolve the real src so the reloaded chip is the actual picture (CanvasImageResolverContext).
    const idMatches = [...marker.matchAll(/Image #(\d+)/g)];
    for (const m of idMatches) push('Image', 'Image', `Image:#${m[1]}`, Number(m[1]));
    // Legacy id-less markers ("…, Image]") — one generic chip; skipped when id'd images matched (the bare
    // word "Image" also precedes " #4", so this would double-count them).
    if (!idMatches.length && /(?:^|[[,\s])Image(?:[\],\s]|$)/.test(marker)) push('Image', 'Image');
  }
  return cards;
};

export const stripSystemContext = (content: string): string => {
  let result = content;
  result = result.replace(/=== DECK ARTIFACT CONTEXT ===[\s\S]*?=== END DECK ARTIFACT CONTEXT ===/g, '');
  
  const figmaPattern = /=== FIGMA DESIGN CONTEXT ===[\s\S]*?=== END FIGMA DESIGN CONTEXT ===/g;
  result = result.replace(figmaPattern, '');
  
  const linearPattern = /=== LINEAR CONTEXT ===[\s\S]*?=== END LINEAR CONTEXT ===/g;
  result = result.replace(linearPattern, '');
  
  const commandPattern = /=== COMMAND: \w+ ===/g;
  result = result.replace(commandPattern, '');

  // Agent-only canvas markers. PlanningChat folds these into `apiPromptText` (never the optimistic display
  // content) so the agent can resolve what the user pointed at — but the BACKEND persists the prompt with
  // them included, so on reload the raw marker showed up in the user's bubble ("[Selected on canvas: Slide
  // 4] Do it now"). The attachment cards already say this visually, so strip them from display.
  result = result.replace(/\[Selected on canvas:[^\]]*\]/g, '');
  result = result.replace(/\[Editing [^\]]*\]/g, '');
  result = result.replace(/\[Document "[^"]*"\s*·\s*documentId:[^\]]*\]/g, '');
  // The artifact agent's own two markers, added after the three above and missed by them:
  // `ArtifactAgent.tsx` prepends `[Artifact focused: slide 14 (…) — of 8 artifact slot(s) on this
  // page]` and `[Artifact style: …]` to the prompt, and both were printing verbatim at the top of
  // the user's bubble on /artifact-agent. Same class of leak, same fix.
  result = result.replace(/\[Artifact focused:[^\]]*\]/g, '');
  result = result.replace(/\[Artifact style:[^\]]*\]/g, '');
  // The brand kit workspace's two markers. Same class as the four above and placed with them, BEFORE
  // the annotations rule below, because that rule eats everything to the end of the string: a kit
  // marker appended after an annotations block would already be gone, and one appended before it has
  // to be matched here or it prints verbatim at the top of the user's bubble on reload.
  result = result.replace(/\[Brand kit workspace:[^\]]*\]/g, '');
  result = result.replace(/\[Kit attachment:[^\]]*\]/g, '');
  // Pointed annotations, from the artifact's own annotate layer. The block is multi-line markdown
  // under a bracketed header, so this eats the header and everything after it.
  //
  // THAT MAKES THE ORDER A CONTRACT, not a convention: the annotations must be the LAST thing in the
  // prompt. Both callers append (`useDeckChat`'s `agentNote`, and /artifact-agent's own send), and
  // `useDeckChat.send`'s doc says why. Put anything after them and this deletes it from the bubble on
  // reload while the server still holds it — a message that reads empty and is not.
  //
  // It cannot be bounded instead. The other markers here are single-line `[Something: …]` and match
  // `[^\]]*`; this block contains CSS selectors, so a non-greedy bracket match stops at the first `]`
  // inside one and leaves the rest of the block printed.
  result = result.replace(/\[Pointed annotations on the artifact[\s\S]*$/g, '');

  result = result.replace(/\\n/g, '\n');
  result = result.replace(/\n{3,}/g, '\n\n');
  result = result.replace(/^\n+|\n+$/g, '');
  
  return result.trim();
};
